import { stringifyPlace } from '../placing'
import { getCurrentRound } from './getCurrentRound'
import {
  type BracketRound,
  type WithGameParticipantId,
  type DecoratedGameParticipant,
  fillRoundsWithMatches,
  hasStarted,
  type BracketGameData,
  SEEDING_ROUND,
} from '@terros/common'

export type TinyBracketGame = Pick<BracketGameData, 'gameId' | 'rounds' | 'status' | 'settings' | 'currentRound'>

export type ParticipantWithSeed = DecoratedGameParticipant & {
  seed?: number
}

export type WithBracketGameData = {
  game: TinyBracketGame
  participants: ParticipantWithSeed[]
}

export const getDisplayBracketRounds = (data: WithBracketGameData): BracketRound[] => {
  const { game } = data

  const numPlayers = getDummyGameBracketSize(data)

  const shouldFill = shouldFillRounds(game)

  if (!shouldFill) return filterSeedingRound(game.rounds)

  return fillMatchesWithDummyUsers(filterSeedingRound(game.rounds), numPlayers)
}

const filterSeedingRound = (rounds: BracketRound[]): BracketRound[] => {
  return rounds.filter((r) => r.round > SEEDING_ROUND)
}

const shouldFillRounds = (game: TinyBracketGame): boolean => {
  if (game.status === 'finished') return false
  if (!game.settings.seedingType) return false
  if (hasStarted(game)) {
    const currentRound = getCurrentRound(game.rounds)
    if (!currentRound) return false
    if (currentRound.round > 0) return false
  }
  return true
}

const GAME_DUMMY_SEED_PREFIX = 'U:GAME_DUMMY_SEED:'
const fillMatchesWithDummyUsers = (rounds: BracketRound[], numPlayers: number): BracketRound[] => {
  const dummyUsers: WithGameParticipantId[] = Array.from({ length: numPlayers }, (_, i) => ({
    participantId: `${GAME_DUMMY_SEED_PREFIX}${i}`,
  }))

  return fillRoundsWithMatches(rounds, dummyUsers)
}

const getDummyGameBracketSize = ({ game, participants }: WithBracketGameData): number => {
  if (game.settings.bracketSize) return game.settings.bracketSize
  return Math.max(2, participants.length)
}

export const isDummyParticipant = (participantId: string): boolean => {
  return participantId.startsWith(GAME_DUMMY_SEED_PREFIX)
}

export const getDummyParticipantPlace = (participantId: string): number => {
  return parseInt(participantId.split(GAME_DUMMY_SEED_PREFIX)[1]) + 1
}

export const getDummyParticipantSeed = (participantId: string): string => {
  return stringifyPlace(getDummyParticipantPlace(participantId)) + ' seed'
}
